import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Calendar, BarChart3, FileText, Zap, Settings, PlusCircle, Clock, CheckCircle, AlertTriangle, Building, Briefcase, Star, MessageSquare, Target, DollarSign, Shield, Activity, ArrowRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';

const AdminDashboard = () => {
  const [range, setRange] = useState('30d');

  useEffect(() => {
    document.title = 'Admin Dashboard - Intime HR Platform';
  }, []);

  const handleCreateEmployee = () => {
    console.log('Navigate to employee directory');
  };

  const handleCreateJob = () => {
    console.log('Navigate to create job posting');
  };

  const handleViewAnalytics = () => {
    console.log('Navigate to analytics');
  };

  const handleViewSettings = () => {
    console.log('Navigate to settings');
  };

  // Configuration banner for backend setup
  const ConfigBanner = () => (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-5 w-5 text-blue-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-blue-700">
            <strong>Backend not configured.</strong> Set NEXT_PUBLIC_API_URL and wire endpoints to enable data.
          </p>
        </div>
      </div>
    </div>
  );

  // Comprehensive metrics covering all HR areas
  const comprehensiveMetrics = [
    // People & Employee Management
    { icon: Users, label: "Total Employees", value: "0", subtitle: "Connect backend to load data", color: "blue", module: "People", link: "/employee-directory" },
    { icon: UserPlus, label: "New Hires (30d)", value: "0", subtitle: "Recent onboarding", color: "green", module: "People", link: "/employee-onboarding" },
    { icon: Building, label: "Departments", value: "0", subtitle: "Organizational units", color: "purple", module: "Organization", link: "/org" },
    
    // Time & Leave Management  
    { icon: Calendar, label: "Time-off Requests", value: "0", subtitle: "Pending approval", color: "orange", module: "Time", link: "/time-off-management" },
    { icon: Clock, label: "Avg. Time to Hire", value: "0 days", subtitle: "Hiring efficiency", color: "cyan", module: "Recruiting", link: "/app/analytics" },
    
    // Performance & Goals
    { icon: Target, label: "Active Goals", value: "0", subtitle: "Employee objectives", color: "indigo", module: "Performance", link: "/performance-management" },
    { icon: Star, label: "Performance Reviews", value: "0", subtitle: "Scheduled this quarter", color: "yellow", module: "Performance", link: "/performance-management" },
    
    // Recruiting & ATS
    { icon: Briefcase, label: "Open Positions", value: "0", subtitle: "Active job postings", color: "teal", module: "Recruiting", link: "/app/jobs" },
    { icon: MessageSquare, label: "Applications", value: "0", subtitle: "Candidate pipeline", color: "pink", module: "Recruiting", link: "/app/pipeline" },
    
    // Documents & Compliance
    { icon: FileText, label: "Policy Documents", value: "0", subtitle: "Company policies", color: "slate", module: "Documents", link: "/docs" },
    { icon: Shield, label: "Compliance Rate", value: "0%", subtitle: "Policy acknowledgments", color: "emerald", module: "Compliance", link: "/docs" },
    
    // Compensation & Finance
    { icon: DollarSign, label: "Comp Bands", value: "0", subtitle: "Salary structures", color: "amber", module: "Compensation", link: "/compensation" }
  ];

  // HR Platform modules with their key capabilities and new pricing tiers
  const hrModules = [
    {
      title: "Employee Directory & Profiles",
      description: "Complete employee management with org chart, profiles, and directory filtering",
      icon: Users,
      color: "blue",
      stats: { employees: "0 employees", active: "0 active" },
      features: ["Employee directory & profiles", "Org chart", "Admin controls and roles"],
      tier: "Starter",
      actions: [
        { label: "Manage Employees", onClick: () => console.log('Navigate to employee directory') },
        { label: "View Org Chart", onClick: () => console.log('Navigate to org chart') }
      ]
    },
    {
      title: "Time-off Management", 
      description: "Complete PTO system with policies, approvals, and calendar integration",
      icon: Calendar,
      color: "green",
      stats: { requests: "0 requests", policies: "0 policies" },
      features: ["Time-off management", "Policy configuration", "Calendar integration"],
      tier: "Starter",
      actions: [
        { label: "Manage Requests", onClick: () => console.log('Navigate to time-off') },
        { label: "Configure Policies", onClick: () => console.log('Navigate to policies') }
      ]
    },
    {
      title: "Onboarding & Checklists",
      description: "Streamlined employee onboarding with customizable checklists and workflows",
      icon: CheckCircle,
      color: "purple",
      stats: { templates: "0 templates", active: "0 active cases" },
      features: ["Onboarding checklists", "Template builder", "Task assignments"],
      tier: "Starter",
      actions: [
        { label: "Manage Onboarding", onClick: () => console.log('Navigate to onboarding') },
        { label: "Create Template", onClick: () => console.log('Create template') }
      ]
    },
    {
      title: "Basic Recruiting/ATS",
      description: "Essential recruiting tools with job posting and candidate tracking",
      icon: Briefcase,
      color: "indigo",
      stats: { jobs: "0 jobs", candidates: "0 candidates" },
      features: ["Basic recruiting/ATS (1 active job post)", "Job posting management", "Candidate tracking"],
      tier: "Starter",
      actions: [
        { label: "Create Job", onClick: handleCreateJob },
        { label: "View Pipeline", onClick: () => console.log('Navigate to pipeline') }
      ]
    },
    {
      title: "Performance Management",
      description: "Advanced performance tracking with goals, reviews, and 1:1s (Growth tier)",
      icon: Target,
      color: "teal",
      stats: { goals: "0 goals", reviews: "0 reviews" },
      features: ["Performance management (goals, reviews, 1:1s)", "Goal tracking", "Review cycles"],
      tier: "Growth",
      actions: [
        { label: "Set Goals", onClick: () => console.log('Navigate to performance') },
        { label: "Schedule Reviews", onClick: () => console.log('Navigate to reviews') }
      ]
    },
    {
      title: "Advanced Analytics",
      description: "AI insights and organizational intelligence (Intelligence tier)",
      icon: BarChart3,
      color: "amber",
      stats: { reports: "0 reports", insights: "0 AI insights" },
      features: ["Advanced analytics + \'Organizational Memory Graph'", "AI insights (attrition prediction)", "HR trend reports"],
      tier: "Intelligence",
      actions: [
        { label: "View Analytics", onClick: handleViewAnalytics },
        { label: "AI Insights", onClick: () => console.log('Navigate to AI insights') }
      ]
    }
  ];

  // Pricing tier information
  const pricingTiers = [
    {
      name: "Starter",
      price: "$6",
      period: "per employee/month",
      minimum: "Minimum monthly fee: $49",
      ideal: "Ideal for 5–50 employees",
      color: "blue"
    },
    {
      name: "Growth", 
      price: "$12",
      period: "per employee/month",
      minimum: "Minimum monthly fee: $99",
      ideal: "Ideal for 50–250 employees", 
      color: "green"
    },
    {
      name: "Intelligence",
      price: "$18", 
      period: "per employee/month",
      minimum: "Minimum monthly fee: $249",
      ideal: "Ideal for 250+ employees",
      color: "purple"
    }
  ];

  // Quick actions for common HR tasks
  const QuickActionsCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Zap className="h-5 w-5 text-blue-600" />
        Quick Actions
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { label: "Add New Employee", icon: UserPlus, onClick: handleCreateEmployee },
          { label: "Create Job Posting", icon: Briefcase, onClick: handleCreateJob },
          { label: "Schedule Review", icon: Calendar, onClick: () => console.log('Schedule review') },
          { label: "Upload Policy", icon: FileText, onClick: () => console.log('Upload policy') },
          { label: "Set Goals", icon: Target, onClick: () => console.log('Set goals') },
          { label: "View Reports", icon: BarChart3, onClick: handleViewAnalytics }
        ]?.map((action, idx) => (
          <button
            key={idx}
            onClick={action?.onClick}
            className="flex items-center gap-2 p-3 text-left rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <action.icon className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">{action?.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Recent activity across all HR modules
  const RecentActivityCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Activity className="h-5 w-5 text-blue-600" />
        Recent Activity
      </h3>
      <EmptyState
        title="No recent activity"
        description="Employee actions, policy updates, and system events will appear here once you start using the platform."
        primaryAction={{
          label: "Get Started",
          onClick: handleCreateEmployee,
          icon: PlusCircle
        }}
        secondaryAction={{
          label: "View All Activity",
          onClick: () => console.log('Navigate to activity feed'),
          icon: ArrowRight
        }}
      />
    </div>
  );

  // Pricing overview card
  const PricingOverviewCard = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <DollarSign className="h-5 w-5 text-green-600" />
        Pricing Overview
      </h3>
      <div className="space-y-3">
        {pricingTiers?.map((tier, index) => (
          <div key={index} className={`p-3 rounded-md border border-${tier?.color}-200 bg-${tier?.color}-50`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`font-semibold text-${tier?.color}-800`}>{tier?.name}</span>
              <span className={`text-${tier?.color}-600 font-bold`}>{tier?.price} {tier?.period}</span>
            </div>
            <p className="text-xs text-gray-600">{tier?.minimum}</p>
            <p className="text-xs text-gray-500">{tier?.ideal}</p>
          </div>
        ))}
        <div className="mt-4 p-3 rounded-md bg-gray-100">
          <p className="text-sm text-gray-700">
            <strong>Free tier</strong> (up to 10 employees) → Directory, Org Chart, and 1 Job Posting only
          </p>
        </div>
      </div>
    </div>
  );

  const pageActions = [
    {
      label: "Add Employee",
      onClick: handleCreateEmployee,
      icon: UserPlus,
      variant: "default"
    },
    {
      label: "View Analytics", 
      onClick: handleViewAnalytics,
      icon: BarChart3,
      variant: "outline"
    },
    {
      label: "Settings",
      onClick: handleViewSettings,
      icon: Settings,
      variant: "outline" 
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Intime HR Platform - Admin Dashboard"
        subtitle="Complete HR platform with People, Time, Performance, Recruiting, Documents & Compensation modules"
        actions={pageActions}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ConfigBanner />
        
        {/* Platform Overview Metrics - Clickable Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {comprehensiveMetrics?.map((metric, index) => (
            <button
              key={index}
              onClick={() => console.log(`Navigate to ${metric?.link}`)}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all text-left"
            >
              <div className="flex items-center">
                <div className={`p-2 rounded-md bg-${metric?.color}-100`}>
                  <metric.icon className={`h-6 w-6 text-${metric?.color}-600`} />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">{metric?.label}</p>
                  <p className="text-2xl font-semibold text-gray-900">{metric?.value}</p>
                  {metric?.subtitle && <p className="text-xs text-gray-500 mt-1">{metric?.subtitle}</p>}
                </div>
              </div>
              <div className="mt-3">
                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full bg-${metric?.color}-50 text-${metric?.color}-700`}>
                  {metric?.module}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* HR Platform Modules with Pricing Tiers */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">HR Platform Modules & Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hrModules?.map((module, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg bg-${module.color}-100`}>
                    <module.icon className={`h-6 w-6 text-${module.color}-600`} />
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      module.tier === 'Starter' ? 'bg-blue-50 text-blue-700' :
                      module.tier === 'Growth'? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'
                    }`}>
                      {module.tier} Tier
                    </span>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{module.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{module.description}</p>
                
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Key Features:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {module.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="space-y-1 mb-4 pt-3 border-t border-gray-100">
                  {Object.entries(module.stats)?.map(([key, value]) => (
                    <div key={key} className="flex justify-between text-xs">
                      <span className="text-gray-500 capitalize">{key}:</span>
                      <span className="text-gray-700 font-medium">{value}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  {module.actions?.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={action?.onClick}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                        idx === 0 
                          ? `bg-${module.color}-600 text-white hover:bg-${module.color}-700`
                          : `text-${module.color}-600 border border-${module.color}-200 hover:bg-${module.color}-50`
                      }`}
                    >
                      {action?.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <QuickActionsCard />
          <RecentActivityCard />
          <PricingOverviewCard />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;